import { useState, useEffect } from 'react';
import { money, originBase } from '../util';

// Imprime la etiqueta de una mesa (para pegar en la mesa y identificarla): sector, número
// de mesa grande, y a nombre de quién está. Usa el diálogo de impresión del navegador.
function printMesaLabel(mesa, sector) {
  const esc = (s) => String(s == null ? '' : s).replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));
  const w = window.open('', '_blank', 'width=440,height=340');
  if (!w) { alert('Habilitá las ventanas emergentes para poder imprimir la etiqueta.'); return; }
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Etiqueta ${esc(mesa.name)}</title>
    <style>
      @page { margin: 8mm; }
      html,body { margin:0; padding:0; }
      body { font-family: Arial, Helvetica, sans-serif; color:#000; text-align:center; padding:26px 20px; }
      .sector { font-size:17px; letter-spacing:.14em; text-transform:uppercase; color:#444; }
      .mesa { font-size:66px; font-weight:800; margin:6px 0 4px; letter-spacing:.02em; }
      .rlabel { font-size:12px; letter-spacing:.12em; text-transform:uppercase; color:#777; margin-top:16px; }
      .resp { font-size:28px; font-weight:700; margin-top:2px; }
    </style></head><body>
      <div class="sector">${esc(sector && sector.name)}</div>
      <div class="mesa">${esc(mesa.name)}</div>
      <div class="rlabel">A nombre de</div>
      <div class="resp">${esc(mesa.responsable || '—')}</div>
      <script>window.onload=function(){setTimeout(function(){window.print();},60);};</script>
    </body></html>`);
  w.document.close();
}

// SERVICIO A LA MESA: interruptor por evento. Apagado, el link de la mesa no muestra el
// botón de pedir. Se activa por noche porque no siempre hay mozas trabajando.
function ServicioMesa({ event, api, mozas }) {
  const [on, setOn] = useState(!!event.servicio_mesa);
  const [guardando, setGuardando] = useState(false);

  async function cambiar(v) {
    setGuardando(true); setOn(v);
    try { await api.patch(`/api/events/${event.id}`, { servicio_mesa: v }); }
    catch (e) { setOn(!v); alert(e.message); }
    finally { setGuardando(false); }
  }

  return (
    <div className="lb-card" style={{ padding: '16px 22px', marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div className="lb-h" style={{ fontSize: 15 }}>🛎️ Servicio a la mesa</div>
          <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 3, lineHeight: 1.5 }}>
            Con esto prendido, cada mesa puede pedir desde su link y una moza se lo lleva.
          </div>
        </div>
        <button className={`lb-btn lb-btn--sm ${on ? 'lb-btn--primary' : 'lb-btn--ghost'}`}
          disabled={guardando} onClick={() => cambiar(!on)}>
          {on ? 'Activado' : 'Desactivado'}
        </button>
      </div>
      {/* Prenderlo sin mozas cargadas deja pedidos que nadie ve: es el error fácil de cometer. */}
      {on && !mozas.length && (
        <div style={{ fontSize: 12, color: 'var(--red-400)', marginTop: 10, lineHeight: 1.5 }}>
          No hay ninguna moza cargada para este evento: los pedidos no le van a llegar a nadie.
          Cargalas en <b>Cajas</b> → Nueva caja → Moza.
        </div>
      )}
      {on && mozas.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 10 }}>
          Mozas: <b style={{ color: '#fff' }}>{mozas.map(m => m.name).join(', ')}</b> · entran por <b style={{ color: '#fff' }}>{originBase()}/mozas</b>
        </div>
      )}
    </div>
  );
}

// Encabezado de sección: número, título grande y una línea que explica para qué sirve.
// La pestaña se arma en el orden en que se usa (sectores → mesas → vender → resumen) y
// cada bloque se separa visualmente: antes eran cuatro tarjetas seguidas sin jerarquía y
// no se entendía qué era configuración y qué era la venta de la noche.
function Seccion({ n, titulo, desc, accion, children, primera }) {
  return (
    <section style={{ marginTop: primera ? 26 : 40 }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap',
        borderTop: primera ? 0 : '1px solid rgba(255,255,255,0.09)', paddingTop: primera ? 0 : 24, marginBottom: 16,
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, color: 'var(--red-400)',
              border: '1px solid rgba(200,16,46,0.5)', width: 28, height: 28, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none',
            }}>{n}</span>
            <div className="lb-display" style={{ fontSize: 23, lineHeight: 1.1 }}>{titulo}</div>
          </div>
          {desc && <div style={{ fontSize: 12.5, color: 'var(--ink-600)', marginTop: 8, lineHeight: 1.55, maxWidth: '68ch' }}>{desc}</div>}
        </div>
        {accion && <div style={{ display: 'flex', gap: 8, flex: 'none' }}>{accion}</div>}
      </div>
      {children}
    </section>
  );
}

// Mesas VIP: el dueño crea sectores (con barra + % consumo) y las mesas disponibles de cada
// sector (capacidad/precio). Para vender: elegir SECTOR → MESA disponible → Generar → link
// para el comprador (con sus N QR de ingreso). Las vendidas desaparecen del selector.
export default function TabMesas({ event, api }) {
  const [sectors, setSectors] = useState([]);
  const [mesas, setMesas] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [result, setResult] = useState(null); // { mesa, qr_codes, credit, public_code }
  const [showSector, setShowSector] = useState(false);
  const [showMesa, setShowMesa] = useState(false);
  const [editMesa, setEditMesa] = useState(null); // mesa en edición, o null

  const load = () => {
    api.get(`/api/mesas/sectors?event_id=${event.id}`).then(setSectors).catch(() => {});
    api.get(`/api/mesas?event_id=${event.id}`).then(setMesas).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get(`/api/tpv/boxes?event_id=${event.id}`).then(setBoxes).catch(() => {});
  }, []); // eslint-disable-line

  const vendidas = mesas.filter(m => m.status === 'sold').length;

  return (
    <div className="lb-fade" style={{ paddingTop: 30 }}>
      <ServicioMesa event={event} api={api} mozas={boxes.filter(b => b.kind === 'moza')} />

      {/* 1 · SECTORES — la configuración de las zonas y su barra */}
      <Seccion
        primera
        n="1"
        titulo="Sectores"
        desc="Cada sector es una zona del boliche (TERRAZA, VIP). Define en qué barra se consume su saldo y qué porcentaje del precio de la mesa vuelve como consumo."
        accion={<button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setShowSector(!showSector)}>{showSector ? 'Cancelar' : '+ Nuevo sector'}</button>}
      >
        {showSector && <SectorForm event={event} api={api} boxes={boxes} onDone={() => { setShowSector(false); load(); }} />}
        {sectors.length === 0
          ? <div className="lb-card" style={{ padding: '20px 22px', fontSize: 13, color: 'var(--ink-600)' }}>Todavía no hay sectores. Creá el primero con <b style={{ color: '#fff' }}>+ Nuevo sector</b>: sin sector no se pueden cargar mesas.</div>
          : (
            <div className="lb-card tk-scroll-x" style={{ padding: '6px 22px' }}>
              <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.4fr 1.4fr 0.8fr 0.8fr auto' }}>
                <span>Sector</span><span>Barra donde se consume</span><span>% consumo</span><span>Mesas</span><span></span>
              </div>
              {sectors.map(s => {
                const ms = mesas.filter(m => m.sector_id === s.id);
                const vend = ms.filter(m => m.status === 'sold').length;
                return (
                  <div key={s.id} className="lb-row" style={{ gridTemplateColumns: '1.4fr 1.4fr 0.8fr 0.8fr auto', alignItems: 'center' }}>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 13, fontFamily: 'var(--font-display)', textTransform: 'uppercase' }}>{s.name}</span>
                    <span style={{ color: s.box_name ? 'var(--ink-800)' : 'var(--red-400)', fontSize: 12.5 }}>{s.box_name || '— sin asignar —'}</span>
                    <span style={{ color: 'var(--ink-800)' }}>{Number(s.consumo_pct)}%</span>
                    <span style={{ color: 'var(--ink-800)' }}>{vend}/{ms.length}</span>
                    <span style={{ textAlign: 'right' }}>
                      <button className="lb-iconbtn" title="Eliminar sector" onClick={async () => { if (window.confirm(`¿Eliminar el sector ${s.name}?`)) { await api.del(`/api/mesas/sectors/${s.id}`); load(); } }}>✕</button>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
      </Seccion>

      {/* 2 · MESAS — las mesas de cada sector */}
      <Seccion
        n="2"
        titulo="Mesas"
        desc="Las mesas se crean en lote dentro de un sector, con su cupo de personas, su precio y su medio de venta. Acá las ves agrupadas por sector, con las vendidas y las que quedan libres."
        accion={<button className="lb-btn lb-btn--white lb-btn--sm" onClick={() => setShowMesa(!showMesa)} disabled={sectors.length === 0}>{showMesa ? 'Cancelar' : '+ Nuevas mesas'}</button>}
      >
        {showMesa && <MesaForm event={event} api={api} sectors={sectors} onDone={() => { setShowMesa(false); load(); }} />}

        {sectors.length === 0
          ? <div className="lb-card" style={{ padding: '20px 22px', fontSize: 13, color: 'var(--ink-600)' }}>Primero creá un sector arriba.</div>
          : <div style={{ fontSize: 12, color: 'var(--ink-600)', marginBottom: 12 }}><b style={{ color: '#fff' }}>{vendidas}</b> de <b style={{ color: '#fff' }}>{mesas.length}</b> mesas vendidas.</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 8 }}>
        {sectors.map(s => {
          const ms = mesas.filter(m => m.sector_id === s.id);
          return (
            <div key={s.id} className="lb-card" style={{ padding: '16px 22px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div className="lb-h" style={{ fontSize: 16 }}>{s.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2 }}>Barra: <b style={{ color: 'var(--ink-800)' }}>{s.box_name || '— sin asignar —'}</b> · Consumo {Number(s.consumo_pct)}%</div>
                </div>
              </div>
              <div className="tk-scroll-x" style={{ marginTop: 12 }}>
                <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.3fr 0.7fr 1fr 1fr 1.1fr auto' }}>
                  <span>Mesa</span><span>Cap.</span><span>Precio</span><span>Consumo</span><span>Estado</span><span></span>
                </div>
                {ms.map(m => {
                  const pct = m.consumo_pct != null ? Number(m.consumo_pct) : Number(m.sector_pct ?? s.consumo_pct);
                  const consumo = Math.round(Number(m.price) * pct / 100);
                  return (
                    <div key={m.id} className="lb-row" style={{ gridTemplateColumns: '1.3fr 0.7fr 1fr 1fr 1.1fr auto', alignItems: 'center' }}>
                      <span style={{ color: '#fff', fontWeight: 600, fontSize: 13, fontFamily: 'var(--font-display)', textTransform: 'uppercase' }}>
                        {m.name}
                        {m.status === 'sold' && <div title="Click para cargar/editar el responsable" onClick={async () => { const v = window.prompt('Nombre del responsable de la mesa:', m.responsable || ''); if (v == null) return; try { await api.patch(`/api/mesas/${m.id}`, { responsable: v.trim() || null }); load(); } catch (e) { alert(e.message); } }} style={{ fontSize: 10.5, color: m.responsable ? 'var(--ink-700)' : 'var(--ink-500)', fontWeight: 400, textTransform: 'none', marginTop: 2, cursor: 'pointer', textDecoration: 'underline dotted' }}>{m.responsable ? `A nombre de ${m.responsable}` : '+ responsable'}</div>}
                      </span>
                      <span style={{ color: 'var(--ink-800)' }}>{m.capacity}</span>
                      <span style={{ color: 'var(--ink-800)' }}>{money(m.price)}</span>
                      <span style={{ color: 'var(--ink-700)', fontSize: 12 }}>{money(consumo)} <span style={{ color: 'var(--ink-600)' }}>({pct}%)</span></span>
                      <span>{m.status === 'sold' ? <span className="lb-status lb-status--paid">Vendida</span> : <span className="lb-status lb-status--gen">Disponible</span>}</span>
                      <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        {m.status === 'sold'
                          ? <>
                              <button className="lb-btn lb-btn--ghost lb-btn--sm" title="Imprimir la etiqueta de la mesa (sector · número · responsable)" onClick={() => printMesaLabel(m, s)}>🏷️ Etiqueta</button>
                              <CopyLink code={m.public_code} />
                              <button className="lb-btn lb-btn--ghost lb-btn--sm" title="Si el comprador olvidó su PIN" onClick={async () => { if (window.confirm('¿Resetear el PIN de esta mesa? El comprador va a crear uno nuevo la próxima vez que abra su link.')) { try { await api.post(`/api/mesas/${m.id}/reset-pin`, {}); alert('PIN reseteado.'); } catch (e) { alert(e.message); } } }}>Reset PIN</button>
                            </>
                          : <>
                              <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setEditMesa(m)}>Editar</button>
                              <button className="lb-iconbtn" onClick={async () => { if (window.confirm('¿Eliminar esta mesa?')) { await api.del(`/api/mesas/${m.id}`); load(); } }}>✕</button>
                            </>}
                      </span>
                    </div>
                  );
                })}
                {ms.length === 0 && <div style={{ padding: '10px 0', fontSize: 12.5, color: 'var(--ink-600)' }}>Sin mesas en este sector. Agregá con "+ Nuevas mesas".</div>}
              </div>
            </div>
          );
        })}
      </div>
      </Seccion>

      {/* 3 · VENDER — generar el link del comprador */}
      <Seccion
        n="3"
        titulo="Vender una mesa"
        desc="Elegí una mesa disponible y generá el link del comprador: adentro va a tener sus entradas y su saldo de consumo, protegidos con un PIN que crea él."
      >
        <GenerarMesa event={event} api={api} sectors={sectors} mesas={mesas}
          onGenerated={(r) => { setResult(r); load(); }} />
      </Seccion>

      {/* 4 · RESUMEN — vendido + saldo otorgado vs consumido */}
      {sectors.length > 0 && (
        <Seccion
          n="4"
          titulo="Resumen por sector"
          desc="Cómo viene la noche: cuántas mesas se vendieron en cada sector, cuánta plata entró y cuánto del saldo de consumo ya se gastó en la barra."
        >
          <SectorSummary sectors={sectors} mesas={mesas} />
        </Seccion>
      )}

      {result && <GenerateResult data={result} onClose={() => setResult(null)} />}
      {editMesa && <EditMesaModal mesa={editMesa} sectors={sectors} api={api} onClose={() => setEditMesa(null)} onSaved={() => { setEditMesa(null); load(); }} />}
    </div>
  );
}

// Editar una mesa disponible (sector, nombre, capacidad, precio, %, medios de venta, quién la genera).
function EditMesaModal({ mesa, sectors, api, onClose, onSaved }) {
  const [f, setF] = useState({
    sector_id: mesa.sector_id || '', name: mesa.name || '', capacity: String(mesa.capacity || 4),
    price: String(mesa.price || ''), consumo_pct: mesa.consumo_pct == null ? '' : String(mesa.consumo_pct),
    roles: mesa.roles || 'owners', channels: Array.isArray(mesa.channels) ? mesa.channels : ['tradicional'],
  });
  const [saving, setSaving] = useState(false);
  const toggleCh = (c) => setF(s => ({ ...s, channels: s.channels.includes(c) ? s.channels.filter(x => x !== c) : [...s.channels, c] }));
  async function save() {
    if (!f.name.trim()) { alert('Nombre'); return; }
    if (!f.channels.length) { alert('Elegí al menos un medio de venta'); return; }
    setSaving(true);
    try {
      await api.patch(`/api/mesas/${mesa.id}`, { sector_id: f.sector_id || null, name: f.name, capacity: +f.capacity || 1, price: +f.price || 0, consumo_pct: f.consumo_pct === '' ? null : +f.consumo_pct, roles: f.roles, channels: f.channels });
      onSaved();
    } catch (e) { alert(e.message); } finally { setSaving(false); }
  }
  return (
    <div className="lb-modal-backdrop" onClick={onClose}>
      <div className="lb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div style={{ padding: '22px 26px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div><div className="lb-eyebrow">Editar mesa</div><div className="lb-h" style={{ fontSize: 20, marginTop: 4 }}>{f.name || 'Sin nombre'}</div></div>
          <button className="lb-iconbtn" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '20px 26px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Sector">
            <select className="lb-select" value={f.sector_id} onChange={e => setF({ ...f, sector_id: e.target.value })}>
              <option value="">— Sin sector —</option>
              {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 0.7fr 1fr 0.9fr', gap: 10 }}>
            <Field label="Nombre"><input className="lb-input" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} /></Field>
            <Field label="Capacidad"><input className="lb-input" inputMode="numeric" value={f.capacity} onChange={e => setF({ ...f, capacity: e.target.value.replace(/\D/g, '') })} /></Field>
            <Field label="Precio"><input className="lb-input" inputMode="numeric" value={f.price} onChange={e => setF({ ...f, price: e.target.value.replace(/\D/g, '') })} /></Field>
            <Field label="% consumo"><input className="lb-input" inputMode="numeric" value={f.consumo_pct} onChange={e => setF({ ...f, consumo_pct: e.target.value.replace(/\D/g, '') })} placeholder="del sector" /></Field>
          </div>
          <div>
            <div className="lb-label" style={{ marginBottom: 8 }}>Medio de venta</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['tradicional', 'Tradicional'], ['online', 'Online']].map(([v, lbl]) => (
                <button key={v} type="button" onClick={() => toggleCh(v)} className={'lb-btn lb-btn--sm ' + (f.channels.includes(v) ? 'lb-btn--primary' : 'lb-btn--ghost')}>{f.channels.includes(v) ? '✓ ' : ''}{lbl}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="lb-label" style={{ marginBottom: 8 }}>Quién la genera (tradicional)</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['owners', 'Solo dueños'], ['all', 'Dueños y RRPP']].map(([v, lbl]) => (
                <button key={v} type="button" onClick={() => setF({ ...f, roles: v })} className={'lb-btn lb-btn--sm ' + (f.roles === v ? 'lb-btn--primary' : 'lb-btn--ghost')}>{lbl}</button>
              ))}
            </div>
          </div>
        </div>
        <div style={{ padding: '16px 26px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={onClose}>Cancelar</button>
          <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    </div>
  );
}

// Reporte por sector: mesas vendidas, monto, y saldo de consumo otorgado vs consumido vs restante.
function SectorSummary({ sectors, mesas }) {
  if (!sectors.length) return null;
  const rows = sectors.map(s => {
    const ms = mesas.filter(m => m.sector_id === s.id);
    const sold = ms.filter(m => m.status === 'sold');
    const vendido = sold.reduce((a, m) => a + Number(m.price || 0), 0);
    const otorgado = sold.reduce((a, m) => a + Number(m.credit_total || 0), 0);
    const restante = sold.reduce((a, m) => a + Number(m.credit_remaining || 0), 0);
    return { s, total: ms.length, vendidas: sold.length, vendido, otorgado, consumido: otorgado - restante, restante };
  });
  const T = rows.reduce((a, r) => ({ vendidas: a.vendidas + r.vendidas, total: a.total + r.total, vendido: a.vendido + r.vendido, otorgado: a.otorgado + r.otorgado, consumido: a.consumido + r.consumido, restante: a.restante + r.restante }), { vendidas: 0, total: 0, vendido: 0, otorgado: 0, consumido: 0, restante: 0 });
  const GC = '1.4fr 0.8fr 1fr 1fr 1fr 1fr';
  return (
    <div>
      <div className="lb-card tk-scroll-x" style={{ padding: '6px 22px' }}>
        <div className="lb-row lb-th" style={{ gridTemplateColumns: GC }}>
          <span>Sector</span><span>Vendidas</span><span>Vendido</span><span>Saldo otorgado</span><span>Consumido</span><span>Restante</span>
        </div>
        {rows.map(r => (
          <div key={r.s.id} className="lb-row" style={{ gridTemplateColumns: GC }}>
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', fontFamily: 'var(--font-display)' }}>{r.s.name}</span>
            <span style={{ color: 'var(--ink-800)' }}>{r.vendidas}/{r.total}</span>
            <span style={{ color: '#fff', fontWeight: 600 }}>{money(r.vendido)}</span>
            <span style={{ color: 'var(--ink-800)' }}>{money(r.otorgado)}</span>
            <span style={{ color: 'var(--ink-800)' }}>{money(r.consumido)}</span>
            <span style={{ color: r.restante > 0 ? '#fff' : 'var(--ink-600)', fontWeight: 600 }}>{money(r.restante)}</span>
          </div>
        ))}
        {rows.length > 1 && (
          <div className="lb-row" style={{ gridTemplateColumns: GC, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
            <span style={{ color: 'var(--ink-700)', fontSize: 12 }}>Total</span>
            <span style={{ color: '#fff', fontWeight: 700 }}>{T.vendidas}/{T.total}</span>
            <span style={{ color: '#fff', fontWeight: 700 }}>{money(T.vendido)}</span>
            <span style={{ color: '#fff', fontWeight: 700 }}>{money(T.otorgado)}</span>
            <span style={{ color: '#fff', fontWeight: 700 }}>{money(T.consumido)}</span>
            <span style={{ color: '#fff', fontWeight: 700 }}>{money(T.restante)}</span>
          </div>
        )}
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ink-600)' }}>Saldo otorgado = consumo cargado a las mesas vendidas. Consumido = gastado en la barra. Restante = lo que queda.</div>
    </div>
  );
}

// Panel de venta: sector → mesa disponible → generar link.
function GenerarMesa({ event, api, sectors, mesas, onGenerated }) {
  const [sectorId, setSectorId] = useState('');
  const [mesaId, setMesaId] = useState('');
  const [loading, setLoading] = useState(false);
  const available = mesas.filter(m => m.sector_id === sectorId && m.status === 'available');
  const mesa = mesas.find(m => m.id === mesaId);

  async function generate() {
    if (!mesaId) return;
    setLoading(true);
    try {
      const r = await api.post(`/api/mesas/${mesaId}/emit`, {});
      onGenerated({ mesa, ...r });
      setMesaId('');
    } catch (e) { alert(e.message); } finally { setLoading(false); }
  }

  return (
    <div className="lb-card" style={{ padding: '22px 24px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.6fr auto', gap: 12, alignItems: 'end' }} className="tk-2col">
        <Field label="Sector">
          <select className="lb-select" value={sectorId} onChange={e => { setSectorId(e.target.value); setMesaId(''); }}>
            <option value="">Elegí el sector</option>
            {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        <Field label="Mesa disponible">
          <select className="lb-select" value={mesaId} onChange={e => setMesaId(e.target.value)} disabled={!sectorId}>
            <option value="">{!sectorId ? 'Elegí primero el sector' : available.length ? 'Elegí la mesa' : 'Sin mesas disponibles'}</option>
            {available.map(m => <option key={m.id} value={m.id}>{m.name} · {m.capacity} personas · {money(m.price)}</option>)}
          </select>
        </Field>
        <button className="lb-btn lb-btn--primary" onClick={generate} disabled={!mesaId || loading}>{loading ? 'Generando…' : 'Generar link →'}</button>
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-600)' }}>Se generan {mesa ? mesa.capacity : 'N'} QR de ingreso + el saldo de consumo. Le pasás el link al comprador.</div>
    </div>
  );
}

function CopyLink({ code }) {
  const [copied, setCopied] = useState(false);
  if (!code) return <span style={{ fontSize: 11, color: 'var(--ink-600)' }}>emitida</span>;
  const url = `${originBase()}/mesa/${code}`;
  return (
    <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => { navigator.clipboard?.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>{copied ? 'Copiado ✓' : 'Copiar link'}</button>
  );
}

function SectorForm({ event, api, boxes, onDone }) {
  const [f, setF] = useState({ name: '', box_id: '', consumo_pct: '50' });
  async function save() {
    if (!f.name.trim()) { alert('Nombre del sector'); return; }
    try { await api.post('/api/mesas/sectors', { event_id: event.id, name: f.name, box_id: f.box_id || null, consumo_pct: +f.consumo_pct || 0 }); onDone(); }
    catch (e) { alert(e.message); }
  }
  return (
    <div className="lb-card" style={{ padding: '18px 22px', marginBottom: 16 }}>
      <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Nuevo sector</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.4fr 0.8fr auto', gap: 12, alignItems: 'end' }} className="tk-2col">
        <Field label="Nombre"><input className="lb-input" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="TERRAZA" /></Field>
        <Field label="Barra donde se consume">
          <select className="lb-select" value={f.box_id} onChange={e => setF({ ...f, box_id: e.target.value })}>
            <option value="">— Elegí la caja —</option>
            {boxes.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </Field>
        <Field label="% consumo"><input className="lb-input" inputMode="numeric" value={f.consumo_pct} onChange={e => setF({ ...f, consumo_pct: e.target.value.replace(/\D/g, '') })} placeholder="50" /></Field>
        <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={save}>Crear</button>
      </div>
    </div>
  );
}

function MesaForm({ event, api, sectors, onDone }) {
  const [f, setF] = useState({ sector_id: sectors[0]?.id || '', quantity: '4', capacity: '4', price: '', consumo_pct: '', roles: 'owners', channels: ['tradicional'] });
  const toggleCh = (c) => setF(s => ({ ...s, channels: s.channels.includes(c) ? s.channels.filter(x => x !== c) : [...s.channels, c] }));
  async function save() {
    const qty = +f.quantity || 0;
    if (qty < 1) { alert('Indicá la cantidad de mesas'); return; }
    if (!f.channels.length) { alert('Elegí al menos un medio de venta'); return; }
    try { await api.post('/api/mesas', { event_id: event.id, sector_id: f.sector_id || null, quantity: qty, capacity: +f.capacity || 1, price: +f.price || 0, consumo_pct: f.consumo_pct === '' ? null : +f.consumo_pct, roles: f.roles, channels: f.channels }); onDone(); }
    catch (e) { alert(e.message); }
  }
  return (
    <div className="lb-card" style={{ padding: '18px 22px', marginBottom: 16 }}>
      <div className="lb-eyebrow" style={{ marginBottom: 12 }}>Nuevas mesas (en lote)</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.9fr 0.9fr 1fr 0.9fr auto', gap: 12, alignItems: 'end' }} className="tk-2col">
        <Field label="Sector">
          <select className="lb-select" value={f.sector_id} onChange={e => setF({ ...f, sector_id: e.target.value })}>
            {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        <Field label="Cantidad de mesas"><input className="lb-input" inputMode="numeric" value={f.quantity} onChange={e => setF({ ...f, quantity: e.target.value.replace(/\D/g, '') })} placeholder="4" /></Field>
        <Field label="Personas por mesa"><input className="lb-input" inputMode="numeric" value={f.capacity} onChange={e => setF({ ...f, capacity: e.target.value.replace(/\D/g, '') })} placeholder="4" /></Field>
        <Field label="Precio"><input className="lb-input" inputMode="numeric" value={f.price} onChange={e => setF({ ...f, price: e.target.value.replace(/\D/g, '') })} placeholder="0" /></Field>
        <Field label="% consumo"><input className="lb-input" inputMode="numeric" value={f.consumo_pct} onChange={e => setF({ ...f, consumo_pct: e.target.value.replace(/\D/g, '') })} placeholder="vacío = del sector" /></Field>
        <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={save}>Crear mesas</button>
      </div>
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span className="lb-label" style={{ margin: 0 }}>Medio de venta:</span>
        {[['tradicional', 'Tradicional'], ['online', 'Online']].map(([v, lbl]) => (
          <button key={v} type="button" onClick={() => toggleCh(v)} className={'lb-btn lb-btn--sm ' + (f.channels.includes(v) ? 'lb-btn--primary' : 'lb-btn--ghost')}>{f.channels.includes(v) ? '✓ ' : ''}{lbl}</button>
        ))}
      </div>
      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span className="lb-label" style={{ margin: 0 }}>Quién la genera (tradicional):</span>
        {[['owners', 'Solo dueños'], ['all', 'Dueños y RRPP']].map(([v, lbl]) => (
          <button key={v} type="button" onClick={() => setF({ ...f, roles: v })} className={'lb-btn lb-btn--sm ' + (f.roles === v ? 'lb-btn--primary' : 'lb-btn--ghost')}>{lbl}</button>
        ))}
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ink-600)' }}>Se crean numeradas solas (MESA 1, MESA 2, …), continuando desde la última del evento. Ej: 4 mesas de 4 personas y después 4 de 8. Si elegís "Dueños y RRPP", los RRPP las ven en su panel (pestaña Mesas).</div>
    </div>
  );
}

function GenerateResult({ data, onClose }) {
  const { mesa, credit, consumo_pct, public_code } = data;
  const cap = mesa?.capacity || data.capacity || 0;
  const [copied, setCopied] = useState(false);
  const url = `${originBase()}/mesa/${public_code}`;
  async function share() {
    if (navigator.share) { try { await navigator.share({ title: 'Tu mesa', text: `Entradas de la mesa ${mesa?.name || ''}`, url }); } catch {} }
    else { navigator.clipboard?.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); }
  }
  return (
    <div className="lb-modal-backdrop" onClick={onClose}>
      <div className="lb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div style={{ padding: '22px 26px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="lb-eyebrow">Mesa generada</div>
          <div className="lb-h" style={{ fontSize: 20, marginTop: 4 }}>{mesa?.name} · {cap} personas</div>
        </div>
        <div style={{ padding: '20px 26px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div className="lb-eyebrow" style={{ marginBottom: 6 }}>Link para el comprador</div>
            <div style={{ fontSize: 12, color: 'var(--ink-800)', wordBreak: 'break-all', fontFamily: 'monospace', marginBottom: 10 }}>{url}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="lb-btn lb-btn--primary lb-btn--sm" style={{ flex: 1 }} onClick={share}>Compartir</button>
              <button className="lb-btn lb-btn--white lb-btn--sm" style={{ flex: 1 }} onClick={() => { navigator.clipboard?.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>{copied ? 'Copiado ✓' : 'Copiar link'}</button>
            </div>
          </div>
          <div style={{ padding: '14px 16px', background: 'rgba(139,15,26,0.10)', border: '1px solid rgba(139,15,26,0.4)', borderRadius: 4 }}>
            <div className="lb-eyebrow" style={{ color: '#fff' }}>Saldo de consumo ({consumo_pct}%)</div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 26, color: '#fff', marginTop: 4 }}>{money(credit.total)}</div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-600)' }}>Pasale el link al comprador: crea un PIN y cada asistente genera su propia entrada (con sus datos). El código de consumo es privado del cliente (queda detrás del PIN).</div>
        </div>
        <div style={{ padding: '16px 26px', borderTop: '1px solid rgba(255,255,255,0.08)', textAlign: 'right' }}>
          <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={onClose}>Listo</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return <div className="lb-field"><div className="lb-label">{label}</div>{children}{hint && <div className="lb-hint">{hint}</div>}</div>;
}
